import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { luxuryHireApi, LuxuryVehicle, BookingQuote, LuxuryAddon } from '../../../services/luxuryHireApi';
import axios from 'axios';

interface Prediction {
  description: string;
  place_id: string;
}

const formatCurrency = (amount: number, currency: string = 'NGN') => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const BookingWizard: React.FC = () => {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const navigate = useNavigate();
  
  const [vehicle, setVehicle] = useState<LuxuryVehicle | null>(null);
  const [addons, setAddons] = useState<LuxuryAddon[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quote, setQuote] = useState<BookingQuote | null>(null);
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    booking_type: 'full_day',
    start_date: '',
    start_time: '09:00',
    end_date: '',
    end_time: '21:00',
    pickup_address: '',
    pickup_lat: null as number | null,
    pickup_lng: null as number | null,
    special_requests: '',
    occasion: '',
    security_included: false,
    selected_addons: [] as number[],
  });
  
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const addressInputRef = useRef<HTMLDivElement>(null);

  const handleAddressSearch = (query: string) => {
    setFormData(prev => ({ ...prev, pickup_address: query }));

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.length < 3) {
      setPredictions([]);
      setShowPredictions(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get('/api/places/autocomplete', {
          params: { input: query },
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (response.data.predictions) {
          setPredictions(response.data.predictions);
          setShowPredictions(true);
        }
      } catch (error) {
        console.error('Address search error:', error);
      }
    }, 300);
  };

  const handlePredictionSelect = async (prediction: Prediction) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/places/details', {
        params: { place_id: prediction.place_id },
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      setFormData(prev => ({
        ...prev,
        pickup_address: response.data.formatted_address,
        pickup_lat: response.data.lat,
        pickup_lng: response.data.lng,
      }));
      setShowPredictions(false);
      setPredictions([]);
    } catch (error) {
      console.error('Location details error:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addressInputRef.current && !addressInputRef.current.contains(event.target as Node)) {
        setShowPredictions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (vehicleId) {
      fetchData(parseInt(vehicleId));
    }
  }, [vehicleId]);

  const fetchData = async (id: number) => {
    try {
      setLoading(true);
      const [vehicleData, addonsData] = await Promise.all([
        luxuryHireApi.getVehicle(id),
        luxuryHireApi.getAddons(true),
      ]);
      setVehicle(vehicleData);
      setAddons(addonsData);
      
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      setFormData(prev => ({
        ...prev,
        start_date: tomorrow.toISOString().split('T')[0],
        end_date: tomorrow.toISOString().split('T')[0],
        security_included: vehicleData.security_required,
      }));
      
      setError(null);
    } catch (err: any) {
      setError('Failed to load booking information.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateEndDateTime = () => {
    const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
    let endDateTime: Date;

    if (formData.booking_type === 'hourly') {
      endDateTime = new Date(startDateTime.getTime() + (vehicle?.minimum_hours || 2) * 60 * 60 * 1000);
    } else if (formData.booking_type === 'half_day') {
      endDateTime = new Date(startDateTime.getTime() + 5 * 60 * 60 * 1000);
    } else if (formData.booking_type === 'full_day') {
      endDateTime = new Date(startDateTime.getTime() + 10 * 60 * 60 * 1000);
    } else {
      endDateTime = new Date(`${formData.end_date}T${formData.end_time}`);
    }

    return endDateTime;
  };

  const fetchQuote = async () => {
    if (!vehicle) return;

    try {
      const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
      const endDateTime = calculateEndDateTime();

      const quoteData = await luxuryHireApi.getQuote({
        vehicle_id: vehicle.id,
        booking_type: formData.booking_type,
        start_datetime: startDateTime.toISOString(),
        end_datetime: endDateTime.toISOString(),
        addons: formData.selected_addons,
        security_included: formData.security_included,
      });
      setQuote(quoteData);
    } catch (err) {
      console.error('Error fetching quote:', err);
    }
  };

  useEffect(() => {
    if (vehicle && formData.start_date && formData.start_time) {
      fetchQuote();
    }
  }, [formData.booking_type, formData.start_date, formData.start_time, formData.end_date, formData.end_time, formData.selected_addons, formData.security_included]);

  const handleSubmit = async () => {
    if (!vehicle) return;

    try {
      setSubmitting(true);
      const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
      const endDateTime = calculateEndDateTime();

      const booking = await luxuryHireApi.createBooking({
        vehicle_id: vehicle.id,
        booking_type: formData.booking_type,
        start_datetime: startDateTime.toISOString(),
        end_datetime: endDateTime.toISOString(),
        pickup_address: formData.pickup_address,
        pickup_latitude: formData.pickup_lat || undefined,
        pickup_longitude: formData.pickup_lng || undefined,
        special_requests: formData.special_requests || undefined,
        occasion: formData.occasion || undefined,
        security_included: formData.security_included,
        addons: formData.selected_addons.map(id => {
          const addon = addons.find(a => a.id === id);
          return addon ? { id: addon.id, name: addon.name, price: addon.price } : null;
        }).filter(Boolean),
      });

      navigate(`/luxury-hire/booking/${booking.id}/confirmation`);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create booking. Please try again.');
      console.error('Error creating booking:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleAddon = (addonId: number) => {
    setFormData(prev => ({
      ...prev,
      selected_addons: prev.selected_addons.includes(addonId)
        ? prev.selected_addons.filter(id => id !== addonId)
        : [...prev.selected_addons, addonId],
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-amber-500/30 rounded-full animate-spin border-t-amber-500"></div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center text-white">
        <p className="text-slate-400 text-lg mb-4">{error || 'Vehicle not found'}</p>
        <button onClick={() => navigate('/luxury-hire')} className="px-6 py-2 bg-amber-500 text-white rounded-full">
          Back to Catalog
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate(`/luxury-hire/vehicle/${vehicleId}`)}
          className="flex items-center text-slate-400 hover:text-white transition-colors mb-6"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Vehicle
        </button>

        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                  step >= s ? 'bg-amber-500 text-white' : 'bg-slate-700 text-slate-400'
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div className={`w-16 h-1 ${step > s ? 'bg-amber-500' : 'bg-slate-700'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="flex justify-center gap-8 text-sm text-slate-400 mb-8">
          <span className={step >= 1 ? 'text-amber-400' : ''}>Date & Time</span>
          <span className={step >= 2 ? 'text-amber-400' : ''}>Details</span>
          <span className={step >= 3 ? 'text-amber-400' : ''}>Review</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {step === 1 && (
              <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
                <h2 className="text-xl font-semibold text-white mb-6">Select Date & Duration</h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Booking Type</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {['hourly', 'half_day', 'full_day', 'multi_day'].map((type) => (
                        <button
                          key={type}
                          onClick={() => setFormData(prev => ({ ...prev, booking_type: type }))}
                          className={`p-3 rounded-xl text-sm font-medium transition-all ${
                            formData.booking_type === type
                              ? 'bg-amber-500 text-white'
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          {type === 'hourly' && 'Hourly'}
                          {type === 'half_day' && 'Half Day'}
                          {type === 'full_day' && 'Full Day'}
                          {type === 'multi_day' && 'Multi-Day'}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Start Date</label>
                      <input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Start Time</label>
                      <input
                        type="time"
                        value={formData.start_time}
                        onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                        className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                  </div>

                  {formData.booking_type === 'multi_day' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">End Date</label>
                        <input
                          type="date"
                          value={formData.end_date}
                          onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                          min={formData.start_date}
                          className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">End Time</label>
                        <input
                          type="time"
                          value={formData.end_time}
                          onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                          className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setStep(2)}
                    disabled={!formData.start_date}
                    className="px-6 py-3 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-colors disabled:opacity-50"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
                  <h2 className="text-xl font-semibold text-white mb-6">Pickup Details</h2>

                  <div className="space-y-4">
                    <div ref={addressInputRef} className="relative">
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Pickup Address
                        <span className="ml-2 text-xs text-amber-400">(Powered by Google Maps)</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={formData.pickup_address}
                          onChange={(e) => handleAddressSearch(e.target.value)}
                          placeholder="Start typing to search for an address..."
                          className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 pr-10"
                        />
                        <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      {showPredictions && predictions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-slate-700 border border-slate-600 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                          {predictions.map((prediction, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => handlePredictionSelect(prediction)}
                              className="w-full text-left px-4 py-3 hover:bg-slate-600 border-b border-slate-600 last:border-b-0 transition-colors"
                            >
                              <div className="flex items-start space-x-3">
                                <svg className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="text-sm text-white">{prediction.description}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      {formData.pickup_lat && (
                        <p className="mt-2 text-xs text-green-400 flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Location verified with GPS coordinates
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Occasion (Optional)</label>
                      <select
                        value={formData.occasion}
                        onChange={(e) => setFormData(prev => ({ ...prev, occasion: e.target.value }))}
                        className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="">Select occasion...</option>
                        <option value="Wedding">Wedding</option>
                        <option value="Birthday">Birthday</option>
                        <option value="Corporate">Corporate Event</option>
                        <option value="Airport">Airport Transfer</option>
                        <option value="Photoshoot">Photoshoot</option>
                        <option value="Anniversary">Anniversary</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Special Requests (Optional)</label>
                      <textarea
                        value={formData.special_requests}
                        onChange={(e) => setFormData(prev => ({ ...prev, special_requests: e.target.value }))}
                        placeholder="Any special requirements or requests..."
                        rows={3}
                        className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                  </div>
                </div>

                {!vehicle.security_required && (
                  <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
                    <h2 className="text-xl font-semibold text-white mb-4">Security Service</h2>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.security_included}
                        onChange={(e) => setFormData(prev => ({ ...prev, security_included: e.target.checked }))}
                        className="sr-only"
                      />
                      <div className={`w-12 h-6 rounded-full transition-colors ${formData.security_included ? 'bg-amber-500' : 'bg-slate-600'}`}>
                        <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${formData.security_included ? 'translate-x-6' : 'translate-x-0.5'} mt-0.5`} />
                      </div>
                      <span className="ml-3 text-slate-300">
                        Add Security Personnel (+{formatCurrency(vehicle.security_fee, vehicle.currency)}/day)
                      </span>
                    </label>
                  </div>
                )}

                {addons.length > 0 && (
                  <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
                    <h2 className="text-xl font-semibold text-white mb-4">Optional Add-ons</h2>
                    <div className="space-y-3">
                      {addons.map((addon) => (
                        <label
                          key={addon.id}
                          className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-colors ${
                            formData.selected_addons.includes(addon.id)
                              ? 'bg-amber-500/20 border-amber-500'
                              : 'bg-slate-700/50 border-slate-600'
                          } border`}
                        >
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.selected_addons.includes(addon.id)}
                              onChange={() => toggleAddon(addon.id)}
                              className="sr-only"
                            />
                            <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                              formData.selected_addons.includes(addon.id)
                                ? 'border-amber-500 bg-amber-500'
                                : 'border-slate-500'
                            }`}>
                              {formData.selected_addons.includes(addon.id) && (
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium">{addon.name}</p>
                              {addon.description && (
                                <p className="text-slate-400 text-sm">{addon.description}</p>
                              )}
                            </div>
                          </div>
                          <span className="text-amber-400 font-medium">
                            +{formatCurrency(addon.price, addon.currency)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-between">
                  <button
                    onClick={() => setStep(1)}
                    className="px-6 py-3 bg-slate-700 text-white rounded-xl font-medium hover:bg-slate-600 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    disabled={!formData.pickup_address}
                    className="px-6 py-3 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-colors disabled:opacity-50"
                  >
                    Review Booking
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
                <h2 className="text-xl font-semibold text-white mb-6">Review Your Booking</h2>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-24 h-24 rounded-xl overflow-hidden bg-slate-700 flex-shrink-0">
                      {vehicle.thumbnail_url ? (
                        <img src={vehicle.thumbnail_url} alt={vehicle.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-4xl">🚗</div>
                      )}
                    </div>
                    <div>
                      <p className="text-amber-400 text-sm font-medium">{vehicle.brand}</p>
                      <h3 className="text-lg font-bold text-white">{vehicle.name}</h3>
                      <p className="text-slate-400">{vehicle.passenger_capacity} passengers</p>
                    </div>
                  </div>

                  <div className="border-t border-slate-700 pt-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Date</span>
                      <span className="text-white">{new Date(formData.start_date).toLocaleDateString('en-NG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Time</span>
                      <span className="text-white">{formData.start_time}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Duration</span>
                      <span className="text-white">{quote?.duration_hours.toFixed(1)} hours</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Pickup</span>
                      <span className="text-white text-right max-w-xs">{formData.pickup_address}</span>
                    </div>
                    {formData.occasion && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Occasion</span>
                        <span className="text-white">{formData.occasion}</span>
                      </div>
                    )}
                  </div>

                  {error && (
                    <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-400 text-sm">
                      {error}
                    </div>
                  )}
                </div>

                <div className="flex justify-between mt-6">
                  <button
                    onClick={() => setStep(2)}
                    className="px-6 py-3 bg-slate-700 text-white rounded-xl font-medium hover:bg-slate-600 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600 transition-colors disabled:opacity-50 shadow-lg shadow-amber-500/25"
                  >
                    {submitting ? 'Creating Booking...' : 'Confirm Booking'}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700 sticky top-8">
              <h3 className="text-lg font-semibold text-white mb-4">Price Summary</h3>
              
              {quote ? (
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Base Price</span>
                    <span className="text-white">{formatCurrency(quote.base_price, quote.currency)}</span>
                  </div>
                  
                  {quote.addons_total > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Add-ons</span>
                      <span className="text-white">{formatCurrency(quote.addons_total, quote.currency)}</span>
                    </div>
                  )}
                  
                  {quote.surge_amount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Peak Pricing ({quote.surge_multiplier}x)</span>
                      <span className="text-white">{formatCurrency(quote.surge_amount, quote.currency)}</span>
                    </div>
                  )}
                  
                  {quote.tax_amount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Tax</span>
                      <span className="text-white">{formatCurrency(quote.tax_amount, quote.currency)}</span>
                    </div>
                  )}
                  
                  <div className="border-t border-slate-700 pt-3 flex justify-between">
                    <span className="text-white font-medium">Total</span>
                    <span className="text-amber-400 font-bold text-xl">{formatCurrency(quote.total_amount, quote.currency)}</span>
                  </div>
                  
                  <div className="bg-slate-700/50 rounded-xl p-4 mt-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-400">Deposit Due Now</span>
                      <span className="text-amber-400 font-semibold">{formatCurrency(quote.deposit_amount, quote.currency)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Balance Due Later</span>
                      <span className="text-white">{formatCurrency(quote.balance_amount, quote.currency)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-slate-400 py-8">
                  Select dates to see pricing
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingWizard;
